import { CSSResult, html, nothing, PropertyValueMap, TemplateResult } from 'lit';
import { LangLitTableView } from '../../../localization/webmodule-common/other/ui/littable-view-lang';
import { lapsedMinutes } from '../general/time';
import { LitElementBase } from '../litelement-base';
import { lockUIandExecute } from '../ui-lock';
import { property, query, state } from 'lit/decorators.js';
import { RequestPage, ResultPaginated } from './RequestPage';
import { TimedTrigger } from '../timed-trigger';
import { userPreferencesChangedEventName, userUIPreferences } from '../context/user-preferences';
import type {
  WebModuleTable,
  WebModuleTableColumnDef,
  WebModuleTableGroupActionTemplate,
  WebModuleTableGroupHeaderTemplate,
  WebModuleTableItemMove
} from '../../components/src/webmodule-components';

// Page length used when a table opts out of paging via `disablePagination()`: high
// enough that every in-memory row lands on page one, so `pageCount` stays 1 and no
// paginator renders.
const unpagedPageLength = 100000;

// Shared instances, not fresh literals: pageSizeOptions() feeds the table's reactive
// `pageSizeOptions` array property on every render, and a new array each time reads as
// a change and re-renders the whole table. Sharing one instance is the whole point.
//
// Frozen for effect, not for type. They are shared process-wide, so an in-place edit here
// would change the options every table in both apps offers - worth making unavailable
// rather than asking people to be careful. `Object.freeze(x)` narrows to `readonly number[]`
// only when you use its return value; calling it as a bare statement leaves the declared
// `number[]` intact, so these still match the component property they are bound to
// (`WebModuleTable.pageSizeOptions: number[]`) - a mismatch Lit template bindings would not
// have type-checked anyway.
const defaultPageSizeOptions: number[] = [10, 25, 50, 100];
const noPageSizeOptions: number[] = [];
Object.freeze(defaultPageSizeOptions);
Object.freeze(noPageSizeOptions);

/**
 * Base class for tables built on the new `<webmodule-table>`. It is a drop-in
 * parallel to {@link LitTableWrapper} (which targets the legacy
 * `<webmodule-lit-table>`): the override surface - `getColumns`,
 * `getRowsFromServer`, `getDefaultSortField`/`getDefaultSortAsc`, `pageLength`,
 * `enableFiltering`/`updateFilter`, `rowClicked`, `getItemRowClass`,
 * `bodyTemplate`/`tableTemplate`/`filterTemplate` - is identical, so an existing
 * table migrates by swapping the base class and the two column-def type
 * identifiers:
 *
 * ```diff
 * - export class FooTable extends LitTableWrapper<FooRow> {
 * + export class FooTable extends DataTableWrapper<FooRow> {
 * -   getColumns(): WebModuleLitTableColumnDef[] {
 * +   getColumns(): WebModuleTableColumnDef[] {
 * -       displayValue: (_t: WebModuleLitTable, item) => ...
 * +       displayValue: (_t: WebModuleTable, item) => ...
 * ```
 *
 * By default it reproduces the legacy wrapper's behaviour exactly (server
 * paging via the `fetch` event, filter input, timed trigger, click rows). The
 * new table's extra capabilities - grouping, pinned/sticky columns, drag & drop
 * reordering, pagination summary + page-size selector - are exposed as opt-in
 * hooks (`groupByField`, `enableDrag`, `showPaginationInfo`, ...) that are
 * inert unless overridden.
 *
 * `getRowClass`/`getColumnClass` are retained for source compatibility but have
 * no effect: the new table styles itself through its shadow DOM.
 */
export class DataTableWrapper<RowClass> extends LitElementBase {
  isDataLoadDelayed() {
    return false;
  }

  lastRefresh: Date = new Date();
  timedTrigger!: TimedTrigger | undefined;
  filterValue: string | null = null;

  @property({ reflect: true })
  class;

  @property({ type: Boolean })
  clickrows = false;

  @property({ type: Boolean })
  sortAscending = false;

  @query('.data-table')
  dataTable?: WebModuleTable | null;

  @state()
  lastFetch: RowClass[] = [];

  @state()
  selectMode: string = 'none';

  constructor() {
    super();
    this.initTimedTrigger();
  }

  private _delayedDataLoad = this.isDataLoadDelayed();
  public get delayedDataLoad() {
    return this._delayedDataLoad;
  }

  @property({ type: Boolean })
  public set delayedDataLoad(value) {
    if (this._delayedDataLoad !== value) {
      this._delayedDataLoad = value;
      this.delayedDataLoadChanged(value);
    }
  }

  connectedCallback(): void {
    super.connectedCallback();

    this.classList.add('div');
    // Marker class so the fill-height flex chain in dealer_ui.scss can target any
    // fill-height table generically, rather than by hard-coded element name.
    if (this.fillHeight()) this.classList.add('wm-table-fill-host');
    window.addEventListener(userPreferencesChangedEventName, this.applyPreferredPageLength);
    // Appearance prefs (elevated/cellBorders/uppercaseHeader) are read in
    // tableTemplate(), so a re-render is all that is needed to pick up a change.
    window.addEventListener(userPreferencesChangedEventName, this.applyPreferredAppearance);
    // resync: a change broadcast while this element was detached was missed
    this.applyPreferredPageLength();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener(userPreferencesChangedEventName, this.applyPreferredPageLength);
    window.removeEventListener(userPreferencesChangedEventName, this.applyPreferredAppearance);
  }

  // Re-render so the appearance bindings (.elevated/.cellBorders/.uppercaseHeader)
  // re-read the preference. requestUpdate is async, so it never races the
  // synchronous applyPreferredPageLength above (which owns the rows-per-page refetch).
  private readonly applyPreferredAppearance = () => this.requestUpdate();

  // The preference value this table last acted on. Keying off the preference
  // transition (not current table state) means a redundant broadcast or a
  // reconnect never stomps a transient rows-per-page the user picked in the
  // table's own selector.
  private lastAppliedRowsPreference = userUIPreferences.current.tableRowsPerPage;

  // Re-fetch when the preferred rows-per-page changes; a no-op for tables whose
  // pageLength override does not derive from the preference.
  private readonly applyPreferredPageLength = () => {
    const preferred = userUIPreferences.current.tableRowsPerPage;
    if (preferred === this.lastAppliedRowsPreference) return;
    this.lastAppliedRowsPreference = preferred;
    const table = this.dataTable;
    const length = this.pageLength();
    if (table && table.pageLength !== length) {
      table.pageLength = length;
      table.pageIndex = 0; // the setter re-fetches even when already 0
    }
  };

  willUpdate(changedProperties) {
    if (changedProperties.has('class')) {
      this.classList.add('div');
      if (this.fillHeight()) this.classList.add('wm-table-fill-host');
    }
  }

  // build and configure the data table.
  connectDataTable() {
    const dataTableElement = this.dataTable;
    if (dataTableElement) {
      dataTableElement.columns = this.getColumns();
      dataTableElement.sortField = this.getDefaultSortField();
      dataTableElement.sortAscending = this.getDefaultSortAsc();
    } else throw new Error("could not bind 'webmodule-table'");
  }

  async getRowsFromServer(request: RequestPage): Promise<ResultPaginated<RowClass>> {
    return {
      count: 0,
      pageCount: 0,
      pageIndex: request.pageIndex,
      pageSize: request.pageSize,
      results: []
    };
  }

  public async rowClicked(_details: CustomEvent<{ table: WebModuleTable; item: RowClass }>) {
    // To be overridden in the derived class and only has an effect if click rows set to true
  }

  pageLength(): number {
    //override as needed; the default follows the user's rows-per-page preference,
    //unless disablePagination() opts the table out of paging altogether
    return this.disablePagination() ? unpagedPageLength : userUIPreferences.current.tableRowsPerPage;
  }

  enableFiltering(): boolean {
    //override as needed
    return false;
  }

  updateFilter(_searchTerm: string | null) {
    //Override as needed
  }

  enableAdvancedFiltering(): boolean {
    //override as needed
    return false;
  }

  getDefaultSortAsc(): boolean {
    //Override as needed
    return false;
  }

  getDefaultSortField(): string | undefined {
    //Override as needed
    return undefined;
  }

  getRowClass(): string {
    //Retained for source compatibility; the new table styles itself.
    return 'row';
  }

  getColumnClass(): string {
    //Retained for source compatibility; the new table styles itself.
    return 'col';
  }

  //must override and provide a correct set of columns, which will render based on the RowType class fields
  getColumns(): WebModuleTableColumnDef[] {
    return [];
  }

  // ---- New-table feature hooks (inert unless overridden) ----

  /** Stable per-row key (enables selection and efficient re-render). Defaults to identity. */
  getRowKey(item: RowClass): unknown {
    return item;
  }

  /** Field name to group rows by. Data should already be ordered by it. */
  groupByField(): string | undefined {
    return undefined;
  }

  /** Returns a group key for an item; takes precedence over {@link groupByField}. */
  getGroupByKey(): ((item: RowClass) => string | null) | undefined {
    return undefined;
  }

  /** Allow groups to collapse by clicking the group header. */
  collapsibleGroups(): boolean {
    return false;
  }

  /** Custom group-header renderer. */
  groupHeaderTemplate(): WebModuleTableGroupHeaderTemplate | undefined {
    return undefined;
  }

  /**
   * Custom renderer for a group header's trailing action slot (e.g. an ellipsis menu). Kept separate from
   * {@link groupHeaderTemplate} because the table renders this into its own slot at the pinned right edge,
   * aligned with the per-row action column — content returned from the header template instead just trails
   * the header text.
   */
  groupActionTemplate(): WebModuleTableGroupActionTemplate | undefined {
    return undefined;
  }

  /** Enable drag & drop row reordering. */
  enableDrag(): boolean {
    return false;
  }

  /** Per-row "is draggable" check; return false to lock (pin) a row. */
  checkEnabledDrag(_item: RowClass): boolean {
    return true;
  }

  /** Where a row dropped onto a group header lands: 'start' or 'end'. */
  groupDropPosition(): 'start' | 'end' {
    return 'end';
  }

  /** Handle a drag/drop reorder (and group-header drops). Override to apply the move. */
  onItemMove(_move: WebModuleTableItemMove): void {
    //Override as needed
  }

  /**
   * Opt a table out of paging entirely: every row renders on one page (the table
   * scrolls) and neither the paginator, the summary nor the rows-per-page selector
   * appears. Default `false`; overriding it drives {@link pageLength},
   * {@link showPaginationInfo} and {@link pageSizeOptions} together, instead of
   * overriding all three per table.
   *
   * Only for tables whose `getRowsFromServer` returns the whole set and ignores the
   * requested `pageSize`: the unpaged {@link pageLength} travels back out on the
   * table's `fetch` event and reaches that hook as `pageSize`, so a table that
   * honours it would ask the server for 100000 rows.
   *
   * Overriding `pageLength` on its own is a different thing and stays perfectly valid:
   * that is how a genuinely server-paged table picks a page size other than the user's
   * preference (see the supplier support inbox, which pages 25 at a time). What to avoid
   * is spelling the opt-out by hand as `pageLength`/`showPaginationInfo`/`pageSizeOptions`
   * - that bypasses this hook, and mixing the two in a class hierarchy gives a
   * half-applied opt-out with no compile error.
   */
  disablePagination(): boolean {
    return false;
  }

  /**
   * Show the "showing X-Y of N / page n of m" pagination summary. Site default
   * is `true` (the summary only renders when there are rows). Override to return
   * `false` to hide it; {@link disablePagination} forces it to `false`.
   */
  showPaginationInfo(): boolean {
    return !this.disablePagination();
  }

  /**
   * Page-size choices for the rows-per-page selector. Site default is
   * `[10, 25, 50, 100]` (the selector renders whenever this is non-empty, with
   * {@link pageLength} as the initial selection). Override to return `[]` to hide
   * the selector; {@link disablePagination} forces it to `[]`.
   */
  pageSizeOptions(): number[] {
    return this.disablePagination() ? noPageSizeOptions : defaultPageSizeOptions;
  }

  /** Horizontal alignment of the paginator within its row. */
  paginatorAlign(): 'left' | 'middle' | 'right' {
    return 'middle';
  }

  /** Optional cap on the scroll-body height, e.g. '60vh', '400px'. */
  bodyMaxHeight(): string | undefined {
    return undefined;
  }

  /** Optional minimum table width to force horizontal scroll, e.g. '1200px'. */
  minTableWidth(): string | undefined {
    return undefined;
  }

  /** Message shown when there are no rows. */
  emptyMessage(): string {
    return '';
  }

  /**
   * Drop shadow around the table. Defaults to the user's "Raised (drop shadow)"
   * preference (off site-wide, matching the flat legacy table). Override in a
   * derived table to force a value regardless of the preference.
   */
  showTableShadow(): boolean {
    return userUIPreferences.current.tableElevated;
  }

  /**
   * Vertical borders between cells. Defaults to the user's "Cell borders"
   * preference (off site-wide, only horizontal row dividers). Override in a
   * derived table to force a value regardless of the preference.
   */
  showCellBorders(): boolean {
    return userUIPreferences.current.tableCellBorders;
  }

  /**
   * Uppercase the header text. Defaults to the user's "Uppercase headers"
   * preference (off site-wide, render the provided text as-is). Override in a
   * derived table to force a value regardless of the preference.
   */
  uppercaseHeaderText(): boolean {
    return userUIPreferences.current.tableUppercaseHeader;
  }

  /**
   * Pin the grand-total footer to the bottom of the scroll viewport. Site default
   * is `false` (the footer flows at the end of the content, matching the flat
   * legacy table). Override to return `true` to keep the totals row sticky.
   */
  stickyFooter(): boolean {
    return false;
  }

  /**
   * Master off switch for per-column cell fading. Individual columns opt in by
   * setting `fade` in {@link getColumns} (e.g. `fade: true` on a long-text
   * column). Override this to return `true` to disable fading across the whole
   * table at once, dropping every column back to plain wrapping cells.
   */
  disableCellFade(): boolean {
    return false;
  }

  /**
   * Make the table fill its (already height-bounded) flex container and own the
   * vertical scroll itself, instead of capping the body at a viewport-relative
   * `max-height`. Default `false` (the table sizes to its content/viewport cap).
   * Override to return `true` when the table sits in a fixed-height region (e.g.
   * a page-control tab) so a second outer scrollbar does not appear beside the
   * table's own scroll. The consuming layout must give the wrapper a bounded
   * flex-column height for this to take effect.
   */
  fillHeight(): boolean {
    return false;
  }

  /**
   * Returns a semantic status variant for a row - `'danger' | 'warning' |
   * 'success' | 'info'` - so the table can tint status rows itself (e.g. overdue
   * rows in red) instead of routing colours through bespoke CSS. Default none.
   */
  getRowVariant(_item: RowClass): string | null | undefined {
    return undefined;
  }

  /**
   * Extra CSS injected into the table's shadow root. Cell, group-header and
   * row-extension content renders inside that shadow DOM, so the page stylesheet
   * cannot reach it; a table that renders rich content styles it from here
   * instead. Accepts a raw CSS string or a Lit `css` result.
   *
   * Return a module-level constant, not a fresh literal per call: this feeds the
   * table's reactive `customStyles` property on every render, and a new value
   * each time re-applies the constructed stylesheet (the same reason the
   * page-size option arrays above are shared instances).
   */
  customStyles(): string | CSSResult | undefined {
    return undefined;
  }

  public filterTemplate(): TemplateResult {
    const resetEvent = this.timedTrigger?.getResetEvent();
    const triggerEvent = this.timedTrigger?.getTriggerEarlyEvent();
    const advancedFilterTemplate = this.enableAdvancedFiltering()
      ? html` <div class="col-7 filter-advanced">${this.advancedFilterTemplate()}</div>`
      : html``;

    return !this.enableFiltering()
      ? html``
      : html`
          <div class="row form-group filter-wrapper">
            ${advancedFilterTemplate}
            <div class="col-5 filter-input">
              <webmodule-input
                size="small"
                class="webmodule-control-left-label"
                label="${LangLitTableView.filter()}:"
                placeholder="${LangLitTableView.textSearch()}"
                autocomplete="off"
                @webmodule-input=${resetEvent}
                @webmodule-blur=${triggerEvent}
                @webmodule-change=${resetEvent}
              ></webmodule-input>
            </div>
          </div>
        `;
  }

  public advancedFilterTemplate(): TemplateResult {
    //override as needed
    return html``;
  }

  // Bound callbacks so `this` is the wrapper even though the table invokes them.
  public readonly itemRowClassEvent = (item: RowClass) => this.getItemRowClass(item);
  private readonly rowVariantEvent = (item: RowClass) => this.getRowVariant(item);
  private readonly rowKeyEvent = (item: RowClass) => this.getRowKey(item);
  private readonly dragEnabledEvent = (item: RowClass) => this.checkEnabledDrag(item);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public getItemRowClass(_item: RowClass) {
    //Override as needed
    return '';
  }

  public tableTemplate(): TemplateResult {
    const groupByKey = this.getGroupByKey() as ((item: object) => string | null) | undefined;
    const headerTemplate = this.groupHeaderTemplate();
    const groupActionTemplate = this.groupActionTemplate();
    return html`
      <webmodule-table
        class="data-table"
        ?fill-height=${this.fillHeight()}
        .data=${this.lastFetch}
        .pageLength=${this.pageLength()}
        ?clickrows=${this.clickrows}
        selectmode="${this.selectMode}"
        .elevated=${this.showTableShadow()}
        .cellBorders=${this.showCellBorders()}
        .uppercaseHeader=${this.uppercaseHeaderText()}
        .stickyFooter=${this.stickyFooter()}
        .disableCellFade=${this.disableCellFade()}
        .keyevent=${this.rowKeyEvent as (item: object) => unknown}
        .itemRowClassEvent=${this.itemRowClassEvent as (item: object) => string}
        .rowVariantEvent=${this.rowVariantEvent as (item: object) => string | null | undefined}
        .groupBy=${this.groupByField()}
        .getGroupByKey=${groupByKey}
        .collapsibleGroups=${this.collapsibleGroups()}
        .groupHeaderTemplate=${headerTemplate}
        .groupActionTemplate=${groupActionTemplate}
        .enableDrag=${this.enableDrag()}
        .checkEnabledDragEvent=${this.dragEnabledEvent as (item: object) => boolean}
        .groupDropPosition=${this.groupDropPosition()}
        .showPaginationInfo=${this.showPaginationInfo()}
        .pageSizeOptions=${this.pageSizeOptions()}
        .paginatorAlign=${this.paginatorAlign()}
        .bodyMaxHeight=${this.bodyMaxHeight()}
        .minTableWidth=${this.minTableWidth()}
        .emptyMessage=${this.emptyMessage()}
        .customStyles=${this.customStyles()}
        @fetch=${(e: CustomEvent) => this.internalDataLoad(e)}
        @rowclick=${(e: CustomEvent<{ table: WebModuleTable; item: RowClass }>) => this.rowClicked(e)}
        @webmodule-table-item-move=${(e: CustomEvent<WebModuleTableItemMove>) => this.onItemMove(e.detail)}
      >
        ${this.tableSlots()}
      </webmodule-table>
    `;
  }

  /** Slotted header content (column header slots). Override to provide HTML headers. */
  public tableSlots(): TemplateResult | typeof nothing {
    return nothing;
  }

  public bodyTemplate(): TemplateResult {
    //override as needed
    return html` <div class="row">
      <div class="col-12">${this.filterTemplate()} ${this.tableTemplate()}</div>
    </div>`;
  }

  render() {
    return html` ${this.bodyTemplate()} `;
  }

  public async refreshData(sinceLastRefreshMinutes?: number): Promise<void> {
    if (this.dataTable && (!sinceLastRefreshMinutes || lapsedMinutes(this.lastRefresh) >= sinceLastRefreshMinutes)) {
      this.dataTable?.fetchEvent();
      this.requestUpdate();
    }
  }

  /**
   * Re-query from the first page. Every filter change must refresh through here
   * (the built-in filter input does so automatically): the current page offset
   * is meaningless against the new result set, and slicing the new set at the
   * old page can land past its end and return an empty page.
   */
  public async resetPageAndRefresh(): Promise<void> {
    if (this.dataTable) {
      this.dataTable.pageIndex = 0; // the setter re-fetches even when already 0
      this.requestUpdate();
    }
  }

  protected delayedDataLoadChanged(value: boolean) {
    if (!value) this.refreshData();
  }

  protected firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.firstUpdated(_changedProperties);
    this.connectDataTable();
  }

  protected async eventHandler(callback: () => Promise<void>) {
    await lockUIandExecute(async () => await callback());
  }

  protected enableTableClass(): string {
    //override
    return '';
  }

  private initTimedTrigger() {
    const triggerEvent = e => {
      this.filterChangeEvent(e);
    };
    this.timedTrigger = new TimedTrigger(1000, triggerEvent);
  }

  private async internalDataLoad(e: CustomEvent) {
    if (this.delayedDataLoad) {
      this.lastFetch = [];
      this.dataTable!.rowCount = 0;
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const index = (e.detail?.pageIndex as number) ?? 0;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const length = (e.detail?.pageLength as number) ?? 20;

    const sortField = (e.detail?.sortField as string) ?? undefined;
    const sortAsc = (e.detail?.sortAscending as boolean) ?? undefined;

    const results = await this.getRowsFromServer({
      pageIndex: index,
      pageSize: length,
      sortAsc,
      sortField
    });

    if (results) {
      this.lastFetch = results.results ?? [];
      this.dataTable!.rowCount = results.count;
    }
  }

  private async filterChangeEvent(e: Event) {
    const searchTerm = (e.target as HTMLInputElement).value;

    this.updateFilter(searchTerm);

    await this.resetPageAndRefresh();
  }
}
